import React, { useEffect, useState, useMemo, useRef } from "react";
import useCrmApi from "../../../components/hook/crm-req";
import { TicketProps, TicketPriority, TicketStatus, PRIORITY_LABELS, STATUS_LABELS, PRIORITY_TEXT_COLOR } from "./types";
import { AlertCircleIcon, CalendarIcon, ClockIcon, TimerIcon, ChevronDown, UserCheck, ExternalLink, Filter } from "lucide-react";
import { RaiseTicketModal } from "../../../components/crm/customer/ticket-pop-up";
import { TeamDetails } from "../../../components/crm/customer/types";
import TicketDetailsModal from "../../../components/crm/tickets/details";
import useRequest from "../../../components/hook/use-req";
import { toast } from "sonner";

// ── helpers ────────────────────────────────────────────────────────────────
function isOverdue(ticket: TicketProps): boolean {
  if (ticket.status === "closed") return false;
  const opened = new Date(ticket.opened_at).getTime();
  const now = Date.now();
  const hoursOpen = (now - opened) / 36e5;
  if (ticket.priority === "critical") return hoursOpen > 0;
  if (ticket.priority === "high") return hoursOpen > 0;
  if (ticket.priority === "medium") return hoursOpen > 0;
  if (ticket.priority === "low") return hoursOpen > 0;
  return hoursOpen > 24;
}

// "New Leads" (and anything else lead-related) is called out in blue in the
// reference design; every other tag stays a plain neutral pill.
const isHighlightedTag = (tag: string) => /lead/i.test(tag);

const getTeamMemberName = (member?: TeamDetails | null): string =>
  member ? `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email : "";

const getAssignedTo = (ticket: TicketProps, team: TeamDetails[]): string => {
  if (!ticket) return "Unassigned";
  const assignedName =
    (ticket as any)?.assigned_to_user_name ||
    (ticket as any)?.assigned_user_name ||
    (ticket as any)?.assignee_name ||
    (ticket as any)?.assigned_to_user?.name ||
    getTeamMemberName((ticket as any)?.assigned_to_user) ||
    getTeamMemberName((ticket as any)?.assigned_user) ||
    getTeamMemberName((ticket as any)?.assignee);
  if (assignedName) return assignedName;
  const member = team.find((m) => m?.id === ticket.assigned_to_user_id);
  return getTeamMemberName(member) || "Assigned";
};

// ── sub-components ─────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: TicketStatus }> = ({ status }) => {
  const map: Record<TicketStatus, string> = {
    open: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
    closed: "bg-slate-100 text-slate-500 dark:bg-slate-500/15 dark:text-slate-400",
    pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${map[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
};

const Detail: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-xs text-slate-400 dark:text-slate-500 mb-0.5">{label}</p>
    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{value}</div>
  </div>
);

// Updated TicketCard with drag handle
const TicketCard: React.FC<{
  ticket: TicketProps;
  team: TeamDetails[];
  onViewDetails: (ticket: TicketProps) => void;
  dragHandleProps?: any;
}> = ({ ticket, team, onViewDetails, dragHandleProps }) => {
  const [expanded, setExpanded] = useState(false);
  const previewText = ticket?.synopsis || ticket?.issue_description || ticket?.issue_type || "No description";

  return (
    <div
      className="rounded-xl mb-3 overflow-hidden border border-indigo-100 dark:border-indigo-500/30 transition-shadow hover:shadow-md cursor-grab active:cursor-grabbing"
      {...dragHandleProps}
    >
      {/* collapsed row */}
      <button
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-indigo-100/70 dark:bg-indigo-500/15 transition-colors text-left"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
      >
        <span className="flex items-center gap-1.5 min-w-0 text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
          {previewText}
        </span>
        <ChevronDown
          size={16}
          className={`text-slate-500 dark:text-slate-400 transition-transform duration-200 shrink-0 ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 pt-3 border-t border-indigo-100 dark:border-indigo-500/30 space-y-3">
          <Detail label="Issue Type" value={ticket.issue_type || "Not specified"} />
          <Detail
            label="Priority"
            value={<span className={`font-semibold ${PRIORITY_TEXT_COLOR[ticket.priority]}`}>{PRIORITY_LABELS[ticket.priority] || "Not set"}</span>}
          />
          <Detail label="Status" value={<StatusBadge status={ticket.status} />} />
          {ticket.issue_description && <Detail label="Issue Description" value={ticket.issue_description} />}

          {ticket.attachment_file_ids?.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500 mb-2">Attachments</p>
              <div className="flex gap-3 flex-wrap">
                {ticket.attachment_file_ids.map((fileUrl, index) => {
                  const lower = fileUrl.toLowerCase();
                  const isImage =
                    lower.endsWith(".png") ||
                    lower.endsWith(".jpg") ||
                    lower.endsWith(".jpeg") ||
                    lower.endsWith(".webp") ||
                    lower.endsWith(".gif");
                  const isPdf = lower.endsWith(".pdf");

                  return (
                    <a
                      key={index}
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group"
                    >
                      {isImage ? (
                        <div className="w-20 h-20 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
                          <img
                            src={fileUrl}
                            alt={`attachment-${index}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col items-center justify-center text-center p-2 hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-colors">
                          <div className="text-2xl mb-1">
                            {isPdf ? "📄" : "📎"}
                          </div>
                          <span className="text-[10px] text-slate-500 dark:text-slate-400 break-all line-clamp-2">
                            {fileUrl.split("/").pop()}
                          </span>
                        </div>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {ticket.tags?.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500 mb-1.5">Tags</p>
              <div className="flex gap-1.5 flex-wrap">
                {ticket.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`text-xs px-2 py-0.5 rounded-md border ${
                      isHighlightedTag(tag)
                        ? "bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-500/30"
                        : "bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700"
                    }`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {ticket.assigned_to_user_id && <Detail label="Assigned to" value={getAssignedTo(ticket, team)} />}

          <div className="flex items-center gap-4 pt-1">
            <button
              onClick={() => onViewDetails(ticket)}
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium underline underline-offset-2"
            >
              View More Details
            </button>
            <a
              href={`/ticketing/${ticket.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium underline underline-offset-2 flex items-center gap-1"
            >
              Open Ticket URL <ExternalLink size={12} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// Draggable Column Component
interface ColConfig {
  label: string;
  tickets: TicketProps[];
  icon: React.ReactNode;
  accentBorder: string;
  accentText: string;
  team: TeamDetails[];
  onViewDetails: (ticket: TicketProps) => void;
  onDrop: (ticketId: string, newPriority: TicketPriority) => void;
  priorityValue: TicketPriority;
}

const Column: React.FC<ColConfig> = ({
  label,
  tickets,
  icon,
  accentText,
  team,
  onViewDetails,
  onDrop,
  priorityValue,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const ticketId = e.dataTransfer.getData("ticketId");
    if (ticketId) {
      onDrop(ticketId, priorityValue);
    }
  };

  return (
    <div
      className={`bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 border-t-4 p-4 shadow-sm min-w-0 transition-colors
        ${isDragOver ? "bg-indigo-50 dark:bg-indigo-500/10 border-indigo-300 dark:border-indigo-500/50" : ""}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* header */}
      <div className="flex items-start gap-2 mb-4">
        <span className={`${accentText} mt-0.5 w-5 h-5 shrink-0`}>{icon}</span>
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200 leading-snug flex-1">
          {label}
        </h2>
      </div>
      {tickets.length === 0 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-10">No tickets</p>
      ) : (
        tickets.map((t) => (
          <TicketCard
            key={t?.id}
            ticket={t}
            team={team}
            onViewDetails={onViewDetails}
            dragHandleProps={{
              draggable: true,
              onDragStart: (e: React.DragEvent) => {
                e.dataTransfer.setData("ticketId", t.id);
                e.dataTransfer.effectAllowed = "move";
              },
            }}
          />
        ))
      )}
    </div>
  );
};

// ── main ───────────────────────────────────────────────────────────────────
const Tickets: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const [tickets, setTickets] = useState<TicketProps[]>([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [team, setTeam] = useState<TeamDetails[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<TicketProps | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | "all">("all");
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false);
  const priorityMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (priorityMenuRef.current && !priorityMenuRef.current.contains(e.target as Node)) {
        setPriorityMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const { makeRequest: getTickets } = useCrmApi(`/tickets`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getTeamList } = useRequest(`/profile/users`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getCurrentUser } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [response] = await getCurrentUser();
      if (response?.payload?.user_id) setCurrentUserId(response.payload.user_id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search is debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Search and the priority filter are both server-side now
  // (GET /tickets?search=…&priority=…), so any change refetches. `loading`
  // is only the very first load — later refetches leave the board on screen
  // rather than flashing a spinner on every keystroke. requestIdRef drops
  // responses from a superseded request so a slow earlier query can't
  // overwrite a newer one.
  const requestIdRef = useRef(0);
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    (async () => {
      try {
        const params: Record<string, string> = {};
        if (debouncedSearch) params.search = debouncedSearch;
        if (priorityFilter !== "all") params.priority = priorityFilter;
        const [response, status] = await getTickets(undefined, params);
        if (requestId !== requestIdRef.current) return;
        if (status >= 200 && status < 300 && Array.isArray(response)) {
          // Filter only open tickets for the board
          setTickets(response.filter((t: TicketProps) => t.status === "open"));
          setError(null);
        } else {
          setError("Failed to load tickets. Please try again.");
        }
      } catch {
        if (requestId === requestIdRef.current) setError("Failed to load tickets. Please try again.");
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, priorityFilter]);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const [response] = await getTeamList();
        if (Array.isArray(response)) {
          setTeam(response);
        } else if (Array.isArray(response?.users)) {
          setTeam(response?.users);
        } else if (Array.isArray(response?.results)) {
          setTeam(response.results);
        }
      } catch {
        setTeam([]);
      }
    };
    fetchTeam();
  }, []);

  // Handle dropping a ticket to change priority
  const handleDrop = async (
    ticketId: string,
    newPriority: TicketPriority
  ) => {
    const ticket = tickets.find((t) => t.id === ticketId);

    if (!ticket) return;

    if (ticket.priority === newPriority) return;

    // optimistic UI update
    setTickets((prev) =>
      prev.map((t) =>
        t.id === ticketId
          ? { ...t, priority: newPriority }
          : t
      )
    );

    try {
      const response = await fetch(
        `https://api.crm.dalmlabs.com/api/v1/tickets/${ticketId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            priority: newPriority,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed");
      }

      toast.success(
        `Moved to ${newPriority} priority`
      );
    } catch (error) {
      // revert if failed
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId ? ticket : t
        )
      );

      toast.error("Failed to update priority");
    }
  };

  // Already narrowed server-side by search + priority (see the fetch effect).
  const filtered = tickets;

  // A synopsis generated in the details drawer shows up on the board card
  // without closing the drawer or refetching.
  const handleSynopsisGenerated = (
    ticketId: string,
    fields: Pick<TicketProps, "synopsis" | "synopsis_generated_at" | "synopsis_model">,
  ) => {
    setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, ...fields } : t)));
  };

  // Tickets assigned to the current logged-in user — a quick-view section
  // above the priority board. Comes from the same server query as the
  // columns, so it follows the active search and priority filter too.
  const myTickets = useMemo(
    () => (currentUserId ? filtered.filter((t) => t.assigned_to_user_id === currentUserId) : []),
    [filtered, currentUserId],
  );

  // Filter tickets with priority (critical, high, medium, low) only, then
  // narrow further to whichever single priority is picked in "Filter by:
  // Priority" — "all" (the default) leaves every column populated.
  const ticketsWithPriority = filtered.filter(t =>
    (t.priority === "critical" ||
      t.priority === "high" ||
      t.priority === "medium" ||
      t.priority === "low") &&
    (priorityFilter === "all" || t.priority === priorityFilter)
  );

  // "Overdue" = only overdue critical tickets
  const critical = ticketsWithPriority.filter(t =>
    t.priority === "critical" && isOverdue(t)
  );

  // High priority tickets (all high priority tickets)
  const high = ticketsWithPriority.filter(t => t.priority === "high");

  // Medium priority tickets (all medium priority tickets)
  const medium = ticketsWithPriority.filter(t => t.priority === "medium");

  // Low priority tickets (all low priority tickets)
  const low = ticketsWithPriority.filter(t => t.priority === "low");

  const handleViewDetails = (ticket: TicketProps) => {
    setSelectedTicket(ticket);
  };

  const columns: Omit<ColConfig, 'onDrop'>[] = [
    {
      label: "Overdue",
      tickets: critical,
      icon: <AlertCircleIcon />,
      accentBorder: "border-t-indigo-500",
      accentText: "text-indigo-500",
      team,
      onViewDetails: handleViewDetails,
      priorityValue: "critical",
    },
    {
      label: "High Priority (Due in 6 hours)",
      tickets: high,
      icon: <TimerIcon />,
      accentBorder: "border-t-indigo-500",
      accentText: "text-indigo-500",
      team,
      onViewDetails: handleViewDetails,
      priorityValue: "high",
    },
    {
      label: "Medium Priority (Due in 12 hours)",
      tickets: medium,
      icon: <ClockIcon />,
      accentBorder: "border-t-indigo-500",
      accentText: "text-indigo-500",
      team,
      onViewDetails: handleViewDetails,
      priorityValue: "medium",
    },
    {
      label: "Low Priority (Due in 24 hours)",
      tickets: low,
      icon: <CalendarIcon />,
      accentBorder: "border-t-indigo-500",
      accentText: "text-indigo-500",
      team,
      onViewDetails: handleViewDetails,
      priorityValue: "low",
    },
  ];

  return (
    <div className="min-h-screen rounded-lg bg-white dark:bg-slate-950 p-6">
      {/* page title */}
      <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight mb-6">
        Tickets
      </h1>

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          New Ticket
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>

        {/* search */}
        <div className="ml-auto flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 w-64 shadow-sm focus-within:ring-2 focus-within:ring-indigo-300 dark:focus-within:ring-indigo-500/40 transition-shadow">
          <svg className="text-slate-400 dark:text-slate-500 shrink-0" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search Tickets"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 outline-none w-full bg-transparent"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 shrink-0"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* priority filter — "all" (default) leaves every column populated;
            picking one narrows the board down to just that priority. */}
        <div className="relative shrink-0" ref={priorityMenuRef}>
          <button
            onClick={() => setPriorityMenuOpen((o) => !o)}
            className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-600 dark:text-slate-300 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <Filter size={14} className="text-slate-400 dark:text-slate-500 shrink-0" />
            Filter by:{" "}
            <span className="font-semibold text-indigo-600 dark:text-indigo-400">
              {priorityFilter === "all" ? "Priority" : PRIORITY_LABELS[priorityFilter]}
            </span>
            <ChevronDown size={14} className={`text-slate-400 dark:text-slate-500 transition-transform shrink-0 ${priorityMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {priorityMenuOpen && (
            <div className="absolute right-0 z-20 mt-1 w-48 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-700 rounded-xl shadow-lg py-1 divide-y divide-slate-100 dark:divide-slate-800">
              {priorityFilter !== "all" && (
                <button
                  onClick={() => {
                    setPriorityFilter("all");
                    setPriorityMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  All Priorities
                </button>
              )}
              {(Object.keys(PRIORITY_LABELS) as TicketPriority[]).map((p) => (
                <button
                  key={p}
                  onClick={() => {
                    setPriorityFilter(p);
                    setPriorityMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  {PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center h-64 gap-3">
          <div className="w-8 h-8 rounded-full border-4 border-indigo-100 dark:border-indigo-500/20 border-t-indigo-600 dark:border-t-indigo-400 animate-spin" />
          <p className="text-sm text-slate-400 dark:text-slate-500">Loading tickets…</p>
        </div>
      )}

      {/* error */}
      {error && !loading && (
        <div className="flex items-center gap-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-400 rounded-xl p-4 text-sm mb-6">
          <AlertCircleIcon />
          {error}
        </div>
      )}

      {/* Assigned to You */}
      {!loading && !error && (
        <div className="mb-6 bg-indigo-50/60 dark:bg-indigo-500/10 rounded-2xl border border-indigo-100 dark:border-indigo-500/30 p-4">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0" />
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Assigned to You</h2>
            <span className="ml-1 shrink-0 text-xs font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-500/20 rounded-full px-2 py-0.5">
              {myTickets.length}
            </span>
          </div>
          {myTickets.length === 0 ? (
            <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-6">No tickets assigned to you.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {myTickets.map((t) => (
                <TicketCard key={t.id} ticket={t} team={team} onViewDetails={handleViewDetails} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* board */}
      {!loading && !error && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {columns.map((col) => (
            <Column
              key={col.label}
              {...col}
              onDrop={handleDrop}
            />
          ))}
        </div>
      )}

      <RaiseTicketModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={(data) => (data)}
        onSaved={(ticket) => setTickets((prev) => [ticket, ...prev])}
      />

      <TicketDetailsModal
        isOpen={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        ticket={selectedTicket!}
        team={team}
        currentUserId={currentUserId}
        onSynopsisGenerated={handleSynopsisGenerated}
        onTicketUpdated={(updated) => {
          setTickets((prev) =>
            prev.map((t) => (t?.id === updated?.id ? updated : t))
          );
          setSelectedTicket(null);
        }}
      />
    </div>
  );
};

export default Tickets;